import { IAdminAiOnboardingBlob } from '@pharmaplan/common';

const PENDING_AUDIO_BLOB_URL_KEY = 'pharmaplan:pending-admin-ai-onboarding-audio';

const fileExtensionByContentType: Record<string, string> = {
  'application/json': 'json',
  'audio/mpeg': 'mp3',
};

export interface IAiOnboardingAudioDownload {
  blobUrl: string;
  fileName: string | null;
}

export const getAiOnboardingFileName = (
  fileName: string | null,
  contentType: string,
) => {
  if (fileName) {
    return fileName;
  }

  const normalizedContentType = contentType.split(';')[0].toLowerCase();
  const extension = fileExtensionByContentType[normalizedContentType] ?? 'mp3';
  const fileType = extension === 'json' ? 'data' : 'audio';

  return `ai-onboarding-${fileType}.${extension}`;
};

export const decodeAiOnboardingBlob = (blob: string) => {
  const byteCharacters = atob(blob);
  const bytes = new Uint8Array(byteCharacters.length);

  for (let index = 0; index < byteCharacters.length; index += 1) {
    bytes[index] = byteCharacters.charCodeAt(index);
  }

  return bytes;
};

export const getPendingAiOnboardingAudioDownload = (): IAiOnboardingAudioDownload | null => {
  const pendingDownload = localStorage.getItem(PENDING_AUDIO_BLOB_URL_KEY);

  if (!pendingDownload) {
    return null;
  }

  try {
    const parsedDownload: unknown = JSON.parse(pendingDownload);

    if (
      !parsedDownload
      || typeof parsedDownload !== 'object'
      || !('blobUrl' in parsedDownload)
    ) {
      return null;
    }

    const { blobUrl, fileName } = parsedDownload as IAiOnboardingAudioDownload;

    if (typeof blobUrl !== 'string') {
      return null;
    }

    return {
      blobUrl,
      fileName: typeof fileName === 'string'
        ? fileName
        : null,
    };
  } catch {
    return {
      blobUrl: pendingDownload,
      fileName: null,
    };
  }
};

export const setPendingAiOnboardingAudioDownload = (
  download: IAiOnboardingAudioDownload,
) => {
  localStorage.setItem(PENDING_AUDIO_BLOB_URL_KEY, JSON.stringify(download));
};

export const clearPendingAiOnboardingAudioBlobUrl = () => {
  localStorage.removeItem(PENDING_AUDIO_BLOB_URL_KEY);
};

export const downloadAiOnboardingAudio = ({
  blob,
  contentType,
}: IAdminAiOnboardingBlob, fileName: string | null) => {
  const fileUrl = URL.createObjectURL(
    new Blob([decodeAiOnboardingBlob(blob)], { type: contentType }),
  );
  const downloadLink = document.createElement('a');

  downloadLink.href = fileUrl;
  downloadLink.download = getAiOnboardingFileName(fileName, contentType);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
  URL.revokeObjectURL(fileUrl);
};
